import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ReportPlanEditor } from "../components/ReportPlanEditor";
import { AppError, toUserMessage } from "../lib/errors";
import {
  createReportRequestUuid,
  REPORT_MAX_SELECTED_PERSPECTIVES,
  REPORT_PERSPECTIVE_OPTIONS,
  REPORT_PURPOSE_MAX_LENGTH,
  REPORT_THEME_MAX_LENGTH,
  type ReportPlanState,
  type ReportRequestedType,
  type ResearchAngle,
  type ReuseCandidate,
  type ReuseCandidateAction,
} from "../lib/report";
import { reportClient } from "../lib/reportClient";
import { writePendingNewsJobSnapshot } from "../lib/newsJob";

type Phase = "input" | "reuseCandidate" | "planReview";

const REUSE_ACTION_LABEL: Record<ReuseCandidateAction, string> = {
  open_previous: "前回のレポートを開く",
  regenerate_latest: "最新情報で作り直す",
  change_perspective: "別の視点で作る",
};

function formatDateTime(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("ja-JP", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function RequestDeepReportPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [phase, setPhase] = useState<Phase>("input");
  const [theme, setTheme] = useState("");
  const [purpose, setPurpose] = useState("");
  const [requestedReportType, setRequestedReportType] = useState<ReportRequestedType | "">("");
  const [selectedPerspectives, setSelectedPerspectives] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);

  const [reuseCandidate, setReuseCandidate] = useState<ReuseCandidate | null>(null);
  const [reuseActions, setReuseActions] = useState<ReuseCandidateAction[]>([]);

  const [planState, setPlanState] = useState<ReportPlanState | null>(null);
  const [angleDraft, setAngleDraft] = useState<ResearchAngle[]>([]);

  const planRequestKeyRef = useRef<string | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const runId = new URLSearchParams(location.search).get("runId");
    if (!runId) return;

    let cancelled = false;
    setRestoring(true);
    reportClient
      .getRun(runId)
      .then((result) => {
        if (cancelled) return;
        setPlanState(result);
        setAngleDraft(result.plan?.angles ?? []);
        if (typeof result.theme === "string") setTheme(result.theme);
        setPhase("planReview");
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(toUserMessage(error));
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePerspective = (value: string) => {
    setSelectedPerspectives((current) => {
      if (current.includes(value)) return current.filter((v) => v !== value);
      if (current.length >= REPORT_MAX_SELECTED_PERSPECTIVES) return current;
      return [...current, value];
    });
  };

  async function handleCreateRun(regenerationMode?: "latest") {
    if (!theme.trim() || submitting) return;

    setSubmitting(true);
    setErrorMessage(null);
    if (!planRequestKeyRef.current) planRequestKeyRef.current = createReportRequestUuid();

    try {
      const result = await reportClient.createRun({
        planRequestKey: planRequestKeyRef.current,
        theme: theme.trim(),
        purpose: purpose.trim() || undefined,
        requestedReportType: requestedReportType || null,
        perspectives: selectedPerspectives,
        ...(regenerationMode ? { regenerationMode } : {}),
      });

      if (result.reuseCandidate) {
        setReuseCandidate(result.reuseCandidate);
        setReuseActions(result.actions);
        setPhase("reuseCandidate");
        return;
      }

      planRequestKeyRef.current = null;
      setPlanState(result);
      setAngleDraft(result.plan?.angles ?? []);
      setPhase("planReview");
      navigate(`/request/deep-report?runId=${encodeURIComponent(result.runId)}`, { replace: true });
    } catch (error) {
      setErrorMessage(toUserMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  function handleReuseAction(action: ReuseCandidateAction) {
    if (action === "open_previous" && reuseCandidate) {
      navigate(`/reports/${encodeURIComponent(reuseCandidate.runId)}`);
      return;
    }

    if (action === "regenerate_latest") {
      setReuseCandidate(null);
      setReuseActions([]);
      void handleCreateRun("latest");
      return;
    }

    // change_perspective: 入力を維持して観点編集へ戻す。内容を変えて出す新規requestなのでkeyは作り直す
    planRequestKeyRef.current = null;
    setReuseCandidate(null);
    setReuseActions([]);
    setPhase("input");
  }

  function isAnglesDirty(): boolean {
    const saved = planState?.plan?.angles ?? [];
    if (saved.length !== angleDraft.length) return true;
    return saved.some((angle, index) => {
      const draft = angleDraft[index];
      return !draft || angle.id !== draft.id || angle.heading !== draft.heading || angle.selected !== draft.selected;
    });
  }

  async function handleStart() {
    if (!planState || busy) return;

    if (angleDraft.filter((angle) => angle.selected).length < 1) {
      setErrorMessage("論点を1件以上選択してください。");
      return;
    }

    setErrorMessage(null);
    setNoticeMessage(null);
    setBusy(true);
    let currentRevision = planState.planRevision;

    try {
      if (isAnglesDirty()) {
        const updated = await reportClient.updatePlan(planState.runId, {
          basePlanRevision: planState.planRevision,
          reportType: planState.plan?.reportType ?? "business",
          angles: angleDraft.map((angle, index) => ({
            id: angle.id,
            heading: angle.heading,
            why: angle.why,
            selected: angle.selected,
            order: index + 1,
          })),
        });
        setPlanState(updated);
        setAngleDraft(updated.plan?.angles ?? []);
        currentRevision = updated.planRevision;
      }

      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createReportRequestUuid();
      const result = await reportClient.startRun(planState.runId, {
        planRevision: currentRevision,
        idempotencyKey: idempotencyKeyRef.current,
      });
      writePendingNewsJobSnapshot({
        id: result.runId,
        jobId: result.runId,
        keyword: theme.trim(),
        notificationType: "email",
        startedAt: new Date().toISOString(),
        mode: "report",
      });
      navigate(`/reports/${encodeURIComponent(result.runId)}`);
    } catch (error) {
      if (error instanceof AppError && error.code === "CONFLICT") {
        try {
          const latest = await reportClient.getRun(planState.runId);
          setPlanState(latest);
          setAngleDraft(latest.plan?.angles ?? []);
          setNoticeMessage("別の更新を反映しました。内容をご確認のうえ、もう一度お試しください。");
        } catch (refetchError) {
          setErrorMessage(toUserMessage(refetchError));
        }
      } else {
        setErrorMessage(toUserMessage(error));
      }
    } finally {
      setBusy(false);
    }
  }

  const canSubmitStep1 = theme.trim().length > 0 && theme.trim().length <= REPORT_THEME_MAX_LENGTH && !submitting;

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">調べたいテーマから作る</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">深掘りレポートを作成</h1>
            </div>
            <Link
              to="/reports"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
            >
              レポート一覧を見る
            </Link>
          </div>
          <p className="mt-2 text-sm font-bold text-[#8A746A]">
            調べたいテーマから、根拠と出典をたどれるレポートを作成します。調査計画の確認までは利用枠を消費しません。
          </p>

          {restoring && <p className="mt-4 text-sm font-bold text-[#8A746A]">読み込んでいます...</p>}
          {noticeMessage && <p className="mt-4 rounded-2xl bg-[#EFF6EE] p-3 text-sm font-bold text-[#4C7A52]">{noticeMessage}</p>}
          {errorMessage && <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>}

          {phase === "input" && !restoring && (
            <form
              className="mt-6 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void handleCreateRun();
              }}
            >
              <label className="block">
                <span className="text-sm font-black text-[#5D4D46]">テーマ *</span>
                <input
                  type="text"
                  value={theme}
                  onChange={(event) => setTheme(event.target.value)}
                  maxLength={REPORT_THEME_MAX_LENGTH}
                  disabled={submitting}
                  placeholder="例: 生成AIの製造業への影響"
                  className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-base text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
                />
              </label>

              <label className="block">
                <span className="text-sm font-black text-[#5D4D46]">目的</span>
                <textarea
                  value={purpose}
                  onChange={(event) => setPurpose(event.target.value)}
                  maxLength={REPORT_PURPOSE_MAX_LENGTH}
                  disabled={submitting}
                  rows={3}
                  placeholder="例: 顧客提案に使える材料が欲しい"
                  className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-base text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
                />
              </label>

              <div>
                <span className="text-sm font-black text-[#5D4D46]">種類</span>
                <div className="mt-2 flex gap-2">
                  {(["business", "general"] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      disabled={submitting}
                      onClick={() => setRequestedReportType((current) => (current === value ? "" : value))}
                      className={`rounded-full border px-4 py-2 text-sm font-black transition ${
                        requestedReportType === value
                          ? "border-[#7C5A4E] bg-[#7C5A4E] text-white"
                          : "border-[#D8C0AF] bg-white text-[#7C5A4E] hover:border-[#A67C52]"
                      }`}
                    >
                      {value === "business" ? "ビジネス" : "一般"}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs font-bold text-[#8A746A]">未選択の場合はAIが推定し、調査計画で確定表示します。</p>
              </div>

              <div>
                <span className="text-sm font-black text-[#5D4D46]">観点</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {REPORT_PERSPECTIVE_OPTIONS.map((option) => {
                    const checked = selectedPerspectives.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={submitting}
                        onClick={() => togglePerspective(option.value)}
                        aria-pressed={checked}
                        className={`rounded-full border px-4 py-2 text-sm font-black transition ${
                          checked
                            ? "border-[#7C5A4E] bg-[#7C5A4E] text-white"
                            : "border-[#D8C0AF] bg-white text-[#7C5A4E] hover:border-[#A67C52]"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={!canSubmitStep1}
                  className="min-h-[50px] rounded-full bg-[#7C5A4E] px-6 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.20)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:bg-[#CBB9AF] disabled:shadow-none"
                >
                  {submitting ? "作成しています..." : "調査計画を作る"}
                </button>
              </div>
            </form>
          )}

          {phase === "reuseCandidate" && reuseCandidate && (
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-[#EADBC8] bg-white/90 p-4">
                <p className="text-sm font-black text-[#4A3E39]">{reuseCandidate.theme}</p>
                {reuseCandidate.summary && <p className="mt-2 text-sm leading-6 text-[#5D4D46]">{reuseCandidate.summary}</p>}
                {reuseCandidate.informationCutoffAt && (
                  <p className="mt-2 text-xs font-bold text-[#8A746A]">
                    情報基準日時: {formatDateTime(reuseCandidate.informationCutoffAt)}
                  </p>
                )}
              </div>
              <p className="text-xs font-bold text-[#8A746A]">
                同じテーマの完成レポートが24時間以内にあります。いずれかを選んでください。
              </p>
              <div className="flex flex-wrap gap-2">
                {reuseActions.map((action) => (
                  <button
                    key={action}
                    type="button"
                    onClick={() => handleReuseAction(action)}
                    className="rounded-full border border-[#D8C0AF] bg-white px-5 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
                  >
                    {REUSE_ACTION_LABEL[action]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {phase === "planReview" && planState?.plan && (
            <div className="mt-6 space-y-5">
              <ReportPlanEditor
                interpretation={planState.plan.interpretation}
                reportType={planState.plan.reportType}
                angles={angleDraft}
                warnings={planState.plan.warnings}
                onChangeAngles={setAngleDraft}
                disabled={busy}
              />

              <p className="text-xs font-bold text-[#8A746A]">この開始で利用枠を1回消費します。</p>

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPhase("input")}
                  disabled={busy}
                  className="rounded-full border border-[#D8C0AF] bg-white px-5 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  入力を修正
                </button>
                <button
                  type="button"
                  onClick={() => void handleStart()}
                  disabled={busy}
                  className="min-h-[50px] rounded-full bg-[#7C5A4E] px-6 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.20)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:bg-[#CBB9AF] disabled:shadow-none"
                >
                  {busy ? "開始しています..." : "この計画で作成を開始"}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
